import React, { useEffect, useState } from 'react';
import SEO from '../components/SEO';
import '../styles.css';

const PRODUCT_SUMMARY = [
  'Since v3.0, Virgil has grown from a desktop screenwriting assistant into a full AI creative workspace for writers and filmmakers, now in public beta with a 14-day free trial.',
  'It now brings writing, storyboards, video editing, original character voices, music, and sound into one creative workspace, alongside multi-provider chat, Agent Mode, and research with sources.',
  'Behind the scenes, Virgil has matured with stronger authentication, Terms of Service enforcement, tenant isolation, secure media delivery, model fallback infrastructure, pricing manifests, desktop auto-updates, and a public-launch security pass.',
];

const ENGINEERING_SUMMARY = [
  'From November 2025 to October 2026, I evolved Virgil from a local Electron/React/Flask screenwriting assistant into a multi-provider AI creative platform, and took it to public beta.',
  'The platform now includes agentic workflows, retrieval, media generation, structured outputs, research surfaces, Auth0-backed access control, Stripe-aware billing infrastructure, secure media delivery, and Mac/Windows release automation.',
  'The work spans full-stack product engineering, AI provider orchestration, realtime Socket.IO UX, Electron packaging, Flask API design, MongoDB persistence, security hardening, model registry tooling, and production launch readiness.',
];

const productEntries = [
  {
    title: "v3.4.2 - New Models and More Reliable Creative Workflows",
    meta: "Released October 7, 2026 for Mac and Windows.",
    intro: "More choices for writing, images, video, and voice, with fixes that help conversations, storyboard work, and usage records stay in sync.",
    bullets: [
      "Updated the OpenAI default to GPT-6.1 Sol, with Maximum reasoning available for demanding creative work. Specific model selection keeps GPT-6 Astra, GPT-6 Luna, and other supported OpenAI models within reach.",
      "Updated the Anthropic and xAI defaults to Claude Opus 5.5 and Grok 4.7. Claude Sonnet 5.5 and Fable 5.1 remain available when you choose a specific model.",
      "Added image choices including FLUX 3 Image, Ideogram 4.5 Precise Edit, and Seedream 5.0 Flash. Nano Banana 2.1 is available for manual selection, while existing image options remain available.",
      "Added Gemini 3.8 Flash and Flash-Lite TTS, plus Eleven v4 and v4 Turbo, with saved voice takes and improved playback.",
      "Fixed large-storyboard reads and missing final answers in Agent Mode. Replies now persist through reloads, with duplicate assistant delivery suppressed.",
      "Improved image-generation and edit usage records, including Nano Banana 2.1, so model costs and usage are recorded consistently without duplicate entries.",
      "Improved handling of generated takes and timing. Original media stays available when you revise a shot; caption and automatic assembly controls require usable timing data.",
      "Improved saved generation status, prompts, and model details so you can review how an image or clip was created.",
    ],
  },
  {
    title: 'v3.4.1 - Video Studio, Character Voices, and Sound',
    meta: 'Released September 22, 2026 for Mac and Windows.',
    intro: 'Bring your storyboards, characters, dialogue, and music into a simpler place to build a scene. Ask Virgil for help or choose the creative tools yourself, with your video and timeline kept in view.',
    bullets: [
      'Redesigned Video Studio with a warm, dark palette and one collapsible, resizable left panel. Switch between Virgil, Video, Characters, Music, Sound, Storyboard, References, and Media without covering your cut.',
      'Generate a clip with a prompt, model, starting image, and simple settings. Model menus now include provider logos and guidance, and new manual video requests start with Seedance when available.',
      'Bring saved storyboard images into video generation as starting frames, or add storyboard and media assets to the timeline with drag and drop or an Add button. Generated images, video, and audio stay together in project history.',
      'Design an original character voice with ElevenLabs: describe how it should sound, listen to the auditions, and save your favorite for later dialogue and character casting.',
      'Keep approved character appearance references and voice choices together in reusable, versioned sets. New supported generations can use those references while existing shots keep the version you selected.',
      'Create voice recordings, dialogue and table reads, music, sound effects, and background ambience. Preview the resulting takes from the media library and add the ones you want to your scene.',
      'Animate a portrait with an existing recording, or use a recording to lip-sync a video. These tools work with selected excerpts and preserve your original audio in the new take; review the result before using it in your cut.',
      'Edit picture, titles, and sound on the timeline, with trimming, splitting, track controls, and Undo. Play handles preview preparation, and Export creates a video from the saved cut.',
      'Find clip actions with a right-click, or use the plus at a selected clip’s end to open Extend. Generating a new continuation is experimental and depends on the source and supported model; review it before adding it to the timeline.',
      'Export ordered storyboard pages as a PDF and an editable treatment package for sharing a pitch. Sora has been retired from new video generation.',
    ],
  },
  {
    title: 'v3.4.0 - A Refined Workspace and More Reliable Writing Tools',
    meta: 'Released September 15, 2026 for Mac and Windows.',
    intro: 'More room for your writing, clearer conversations, and smoother movement from an idea in chat to a proposal in your screenplay.',
    bullets: [
      'Consolidated the project title and workspace tabs into a compact desktop header, with softer panel edges and clearer space between the editor and chat.',
      'Refreshed Projects, Settings, and the Style Library. Project search, sorting, and a single scrolling list make it easier to find your work.',
      'Brought conversation titles and tabs together, improved title persistence, fixed outline ruler scrolling, and removed the length limit on custom AI instructions.',
      'Chat now uses AI to recognize when a request needs Agent Mode, including follow-up instructions, while preserving your selected model. Unclear requests get a choice before switching.',
      'Fixed duplicate creative replies and working indicators that could remain after a task finished. Working cards now stay out of the way while responses stream.',
      'Fixed insertion proposals that could leave an empty screenplay blank, and improved the handling of accepted edits, review completion, and Undo.',
      'Fixed imported video duration so clips retain their correct length in the library and timeline.',
      'Polished the guided demo with clearer scene formatting, smoother navigation, and a working copy-and-paste flow into the screenplay editor.',
      'Updated the Fireworks-backed DeepSeek and Qwen options to DeepSeek V4 Pro 0813 and Qwen 3.8 Max, and improved chat completion, connection recovery, and cancellation handling.',
      'Upgraded the desktop runtime to Electron 44. This version requires macOS 13 or newer on Apple silicon; Windows remains supported.',
    ],
  },
  {
    title: "v3.3.6 - In-App Support, Agent Media Fixes, And New Image Models",
    meta: "Released August 27, 2026 for Mac and Windows.",
    bullets: [
      "Added a Support tab in Settings so you can report bugs or reach the team without leaving the app. Replies go straight to your email.",
      "Fixed agent-driven image and video generation issues so media requests made through Agent Mode complete reliably.",
      "Added Recraft 4.1 image models and MiniMax H3 Max video generation.",
      "Refreshed the model catalog and tightened image model pricing checks.",
    ],
  },
  {
    title: "v3.3.5 - Document Upload Improvements",
    meta: "Released August 26, 2026 for Mac and Windows.",
    bullets: [
      "Improved document uploads across the app, making it smoother to bring your own manuscripts and pages into Virgil.",
    ],
  },
  {
    title: "v3.3.4 - Faster, More Reliable Releases",
    meta: "Released August 24, 2026 for Mac and Windows.",
    bullets: [
      "Optimized the release pipeline so updates build and ship faster on both platforms.",
    ],
  },
  {
    title: "v3.3.3 - Update Flow Fixes",
    meta: "Released August 24, 2026 for Mac and Windows.",
    bullets: [
      "Fixed issues in the desktop auto-update flow so new versions install more smoothly.",
    ],
  },
  {
    title: "v3.3.2 - Usage Dashboard Upgrade",
    meta: "Released August 24, 2026 for Mac and Windows.",
    bullets: [
      "Upgraded the in-app usage dashboard with model rankings, most-used and highest-cost insights, and clearer estimates of what your usage is worth.",
      "Improved trial and complimentary billing states so what you owe (or do not owe) is always explicit.",
      "Fixed video thumbnails in the media library.",
    ],
  },
  {
    title: "v3.3.1 - Sign-In Reliability",
    meta: "Released August 20, 2026 for Mac and Windows.",
    bullets: [
      "Fixed a sign-in bug that could prevent conversations from loading after login.",
    ],
  },
  {
    title: "v3.3.0 - Public Beta Launch",
    meta: "Released August 19, 2026 for Mac and Windows.",
    intro: "Virgil opened its doors. The public beta brings a free trial, transparent usage budgets, and a new website with a hosted live demo.",
    bullets: [
      "Launched the public beta with a 14-day free trial including $20 of AI usage, protected by a hard budget cap so trials can never overspend.",
      "Added beta seat reservations and a streamlined checkout for Starter, Pro, and Studio plans.",
      "Connected trial billing end to end with Stripe, including usage metering for tokens, images, and video.",
      "Shipped the new virgil.com.ai with a hosted, guided demo of the real app.",
    ],
  },
  {
    title: "v3.2.9 - Launch Readiness And Native Claude",
    meta: "Released August 18, 2026 for Mac and Windows.",
    bullets: [
      "Moved Claude integration to the native Anthropic SDK for faster, more reliable agent runs.",
      "Improved agent behavior and fixed UI issues in outline editing.",
      "Qualified every image and video model in a full pre-launch audit, with alerting for billing integrity and model routing health.",
      "Introduced model tiering so each plan gets the right mix of speed, quality, and cost.",
    ],
  },
  {
    title: "v3.2.8 - Agent Stability",
    meta: "Released August 12, 2026 for Mac and Windows.",
    bullets: [
      "Fixed agent glitches that could interrupt longer runs.",
    ],
  },
  {
    title: "v3.2.7 - Video Studio Polish",
    meta: "Released August 12, 2026 for Mac and Windows.",
    bullets: [
      "Fixed mute and solo controls on the Video Studio timeline.",
      "Final stability fixes across the editing surfaces.",
    ],
  },
  {
    title: "v3.2.6 - Studio Agent, Video Editor Flow, And A Bigger Model Catalog",
    meta: "Released August 4, 2026 for Mac and Windows. Includes July work.",
    intro: "A summer of work on making Virgil's agent a real production partner: it can now drive the storyboard and video editor, not just the page.",
    bullets: [
      "Taught Agent Mode to operate the storyboard and Video Studio, so one request can flow from script to shots to a cut.",
      "Added the Virgil dock and proposal tray, giving agent suggestions a proper home in the UI.",
      "Added a new model selector with an Auto mode that picks the cheapest model capable of the job and escalates when the work gets hard.",
      "Expanded the model catalog with new video generation models including Seedance, Wan, Kling, and Hailuo.",
      "Improved the hosted demo and web experience.",
    ],
  },
  {
    title: 'v3.2.5 - Public Launch Readiness',
    meta: 'Released June 24, 2026 for Mac and Windows.',
    intro: 'Virgil is being hardened for a larger public audience. This release focuses on trust, reliability, safer media handling, and keeping the app current as AI models evolve.',
    bullets: [
      'Added a Terms of Service acceptance flow directly inside the app.',
      'Strengthened account, project, conversation, media, and editor protections across the product.',
      'Made storyboard and character images more secure by moving protected media away from token-in-URL loading.',
      'Added a model registry so Virgil can keep pace with new OpenAI, Claude, Gemini, Grok, OSS, DeepSeek, Qwen, Kimi, video, and image models.',
      'Added smarter model fallback and pricing infrastructure so the app can remain reliable even as providers change model names, pricing, and availability.',
      'Added a new structured output window for code, commands, JSON, SQL, checklists, plans, schemas, and tables.',
      'Completed a public launch security pass covering auth, CORS, HTML sanitization, media downloads, update delivery, and provider route access.',
    ],
  },
  {
    title: 'Also In v3.2.5 - Research Surface',
    meta: 'April 2026 work, shipped June 24, 2026 in v3.2.5.',
    intro: 'Research answers now feel like a first-class part of Virgil instead of a plain chat response. Writers can ask deeper questions and see the sources, progress, and context behind the answer.',
    bullets: [
      'Added live research progress so users can see what Virgil is doing while it gathers information.',
      'Added source cards, source rails, and expandable source drawers for richer cited answers.',
      'Created a shared research experience for normal chat and Agent Mode.',
      'Improved the chat renderer so research, creative writing, edit diffs, and structured outputs each get their own dedicated presentation.',
      'Polished the visual system across chat, creative windows, diff windows, screenplay views, and the main app shell.',
    ],
  },
  {
    title: 'v3.2.4 - Claude Agent Reliability',
    meta: 'Released March 20, 2026 for Mac and Windows.',
    bullets: [
      'Improved Claude-powered Agent Mode so longer writing and editing runs feel smoother and more dependable.',
      'Made agent activity easier to follow across chat, screenplay, and outline windows.',
      'Strengthened the live bridge that keeps multi-window agent sessions in sync.',
    ],
  },
  {
    title: 'v3.2.3 - Build And Release Maintenance',
    meta: 'Released March 20, 2026 for Mac and Windows.',
    bullets: [
      'Updated Mac and Windows build workflows.',
      'Improved release consistency across desktop platforms.',
    ],
  },
  {
    title: 'v3.2.2 - Claude Runtime Fixes',
    meta: 'Released March 19, 2026 for Mac and Windows.',
    bullets: [
      'Fixed Claude runtime behavior after the larger Agent Mode updates.',
      'Improved screenplay change-set handling and backend reliability.',
    ],
  },
  {
    title: 'v3.2.1 - Storyboard, Video, And Backend Patch',
    meta: 'Released March 5, 2026 for Mac and Windows.',
    bullets: [
      'Improved storyboard and video generation flows.',
      'Smoothed generated media handling and UI state.',
      'Added release validation fixes for packaged desktop builds.',
    ],
  },
  {
    title: 'v3.2.0 - Style Library, Retrieval, And Video Capabilities',
    meta: 'Released March 3, 2026 for Mac and Windows.',
    intro: 'Virgil became more context-aware. This release added the foundation for style memory, richer project retrieval, and broader media generation.',
    bullets: [
      'Added style library functionality so Virgil can remember and reuse writing style context.',
      'Added retrieval infrastructure for conversations, documents, outlines, embeddings, and script context.',
      'Expanded video provider capabilities, including xAI/Grok video support.',
      'Improved storyboard, media, and video generation routes.',
      'Refreshed app icons, update progress screens, packaging, and desktop update behavior.',
    ],
  },
  {
    title: 'v3.1.8 - v3.1.12 - Backend Release Train',
    meta: 'Released March 3, 2026 for Mac and Windows.',
    bullets: [
      'Shipped a fast sequence of backend and desktop patches leading into v3.2.0.',
      'Improved provider route cleanup, versioning, release metadata, and media behavior.',
    ],
  },
  {
    title: 'v3.1.7 - Release Polish',
    meta: 'Released February 27, 2026 for Mac and Windows.',
    bullets: [
      'Continued desktop release polish after updater and icon improvements.',
      'Kept Mac and Windows packaging aligned.',
    ],
  },
  {
    title: 'v3.1.6 - Logo And Updater Fixes',
    meta: 'Released February 27, 2026 for Mac and Windows.',
    bullets: [
      "Updated Virgil's branding assets.",
      'Improved packaged desktop updater behavior.',
    ],
  },
  {
    title: 'v3.1.5 - Emergency Agent Fixes',
    meta: 'Released February 21, 2026 for Mac and Windows.',
    bullets: [
      'Fixed urgent Agent Mode scoping issues.',
      'Corrected Claude configuration.',
      'Stabilized agent behavior after the tool-execution updates.',
    ],
  },
  {
    title: 'v3.1.4 - Desktop Updater Reliability',
    meta: 'Released February 21, 2026 for Mac and Windows.',
    bullets: [
      'Improved auto-updater reliability across desktop platforms.',
      'Tightened packaged release behavior for Mac and Windows.',
    ],
  },
  {
    title: 'v3.1.2 - v3.1.3 - Release Maintenance',
    meta: 'Released February 21, 2026 for Mac and Windows.',
    bullets: [
      'Shipped release metadata and package updates following v3.1.1.',
      'Kept the patch line moving while hotfixes were validated.',
    ],
  },
  {
    title: 'v3.1.1 - Web Foundation, Cloud Saving, UI Overhaul, And Tool Cancellation',
    meta: 'Released February 21, 2026 for Mac and Windows.',
    intro: 'This release pushed Virgil beyond a desktop-only app and toward a more flexible creative workspace.',
    bullets: [
      'Added a web-capable foundation so Virgil can run beyond the Electron desktop path.',
      'Added cloud-oriented project and conversation persistence work.',
      'Overhauled major parts of the UI, including chat, project management, video generation, screenplay tools, settings, and app styling.',
      'Improved video editing and video generation across multiple providers.',
      'Added style library functionality for reusable creative voice and tone.',
      'Improved straight-chat tool execution and cancellation across providers.',
      'Fixed AWS, video generation, outline window, backend latency, vector infrastructure, and other release-blocking issues.',
    ],
  },
  {
    title: 'v3.1.0 - Multi-Provider Agents',
    meta: 'Released December 19, 2025 for Mac and Windows. Also tagged as v3.1 before the semver correction.',
    intro: 'Virgil became a true multi-model writing environment, with stronger Agent Mode and a much broader provider lineup.',
    bullets: [
      'Expanded support across Claude, OpenAI, Gemini, Grok, DeepSeek, Qwen, Kimi, and OSS models.',
      'Added model icons and a more polished model selection experience.',
      'Improved autonomous agent execution, writing tools, edit tools, outline tools, communication tools, and web-search tools.',
      'Added richer real-time status indicators so users can see what the agent is doing.',
      'Improved detached windows and live sync across chat, outline, and agent status.',
      'Continued UI polish across chat, video generation, settings, and screenplay editing.',
    ],
  },
  {
    title: 'v3.0.1 - Windows Release Fixes',
    meta: 'Released November 26, 2025 for Windows.',
    bullets: [
      'Fixed Windows packaging and startup issues after the v3.0 launch.',
      'Added small UI polish and updated model pricing data.',
      'Improved screenplay multi-edit controls, storyboard styling, and billing metadata.',
    ],
  },
  {
    title: 'v3.0 - Video Generation',
    meta: 'Released November 20, 2025 for Mac and Windows.',
    intro: 'Virgil moved from screenwriting assistant into visual development partner with the first major video generation release.',
    bullets: [
      'Added a dedicated video generation window.',
      'Added backend infrastructure for video routes, media handling, provider pricing, and provider abstractions.',
      'Added provider support paths for OpenAI, Google, Runway, Luma, Kling, Hailuo, and other video systems.',
      'Connected video generation into storyboard and media workflows.',
      'Updated billing and cost tracking for video and model usage.',
      'Improved Gemini UI behavior, chat styling, and app layout.',
    ],
  },
];

const engineeringEntries = [
  {
    title: "v3.4.2 - Model Routing, Agent Persistence, and Usage Accounting",
    meta: "Released October 7, 2026 for Mac and Windows.",
    intro: "Updated provider integrations and strengthened the contracts between agent tools, saved project state, media output, and billing records.",
    bullets: [
      "Aligned model roles and environment overrides, updated provider defaults, and mapped GPT-6 reasoning settings to supported request parameters while preserving explicit model choices.",
      "Integrated the new image and voice options with provider capability checks and pricing. Nano Banana 2.1 is enabled for manual selection and excluded from automatic model selection.",
      "Fixed storyboard read pagination and request context, assistant-message persistence, stale connection handling, and duplicate assistant delivery across agent workflows.",
      "Strengthened canonical usage identity and deduplication, Google media usage normalization, and retention of raw provider usage for cost reconciliation.",
      "Preserved original media across revisions and enforced timing requirements for caption and automatic assembly paths when speech output has no usable alignment.",
      "Validated 1,425 Python tests and 574 frontend checks, plus type checking and builds. Live checks covered Nano Banana 2.1 generation and reference editing, usage receipts, production model catalogs, and a short saved-cut export.",
      "Published Mac and Windows artifacts, verified Mac signing and notarization, and checked the production export against its stored checksum and media metadata.",
    ],
  },
  {
    title: 'v3.4.1 - Video Production, Shared Audio Tools, and Rendering',
    meta: 'Released September 22, 2026 for Mac and Windows.',
    intro: 'Connected the simpler Studio interface to shared production, audio, media, and timeline services across desktop and web.',
    bullets: [
      'Added versioned character references and voice casting, with owned source identities and explicit bindings for supported generation paths. Saved revisions preserve the inputs used by existing shots; they do not guarantee generated likeness.',
      'Integrated the official ElevenLabs SDK for synthetic Voice Design auditions and reusable saved voices, speech, music, sound effects, and audio processing. Temporary audition IDs cannot be used as a saved character voice.',
      'Shared reviewed audio, performance, and continuation proposals across generic, Claude, and Gemini agent runtimes, with durable request identities, usage accounting, and recovery that avoids resubmitting uncertain paid requests.',
      'Added portrait-plus-recording and video lip-sync workflows with measured excerpts and original-audio preservation. Performance and synchronization quality remain subject to review.',
      'Implemented source-bound clip continuation, explicit review, and undoable timeline placement. Automated checks cover preparation and editing contracts; successful live provider completion remains unverified for the new continuation path.',
      'Expanded the revisioned timeline, shared preview/export composition, chunked rendering, and dedicated render worker deployment. Added audiovisual inspection with source ranges and stated sampling limits.',
      'Connected the left-panel libraries to saved storyboards, character sets, and project media; added model logos, responsive controls, playback coordination, and draft retention while switching tools.',
      'Validated focused service and UI checks, frontend builds, and short live development flows for voice design, saved-voice reuse, sound, recorded performance, and export. Long-form quality acceptance and full autonomous production remain separate work.',
    ],
  },
  {
    title: 'v3.4.0 - Electron 44 and Chat-to-Agent Reliability',
    meta: 'Released September 15, 2026 for Mac and Windows.',
    intro: 'A desktop runtime upgrade paired with shared fixes across provider dispatch, streaming, conversation state, and screenplay review.',
    bullets: [
      'Upgraded to Electron 44.3.0, electron-builder 26.15.3, and electron-updater 6.8.9, with Node 24 build tooling and macOS compatibility checks in update metadata.',
      'Added an AI execution-mode preflight using bounded conversation context, scoped submission locks, cancellation, and a chat fallback when routing is unavailable.',
      'Hardened desktop and web Agent startup, authenticated socket-room joins, late-event handling, and conversation-scoped completion so finished tasks cannot be revived by delayed events.',
      'Unified status visibility across text streams and creative, structured, and edit previews, with delayed reappearance and immediate terminal cleanup.',
      'Fixed OpenAI creative-response duplication, Gemini completion handling, and conversation-title persistence; updated DeepSeek and Qwen model settings with matching usage pricing.',
      'Recovered stale insertion anchors only in an unambiguous empty screenplay, and added pending-proposal recovery without replaying an active review.',
      'Corrected desktop demo scrolling for scaled canvases and routed its scene paste through the real editor paste handler.',
      'Added focused regression coverage for routing, startup, provider completion, status visibility, proposal display, and review decisions. Provider availability and signed release validation remain separate checks.',
    ],
  },
  {
    title: 'v3.2.5 - Public Launch Readiness',
    meta: 'Released June 24, 2026 for Mac and Windows.',
    intro: 'This cycle moved Virgil from feature-rich prototype toward production-ready SaaS infrastructure.',
    bullets: [
      'Designed and implemented a versioned, Mongo-backed Terms of Service gate enforced after authentication.',
      'Hardened tenant isolation by removing trust in client-supplied user IDs across project, editor, conversation, save, search, and media routes.',
      'Added auth enforcement to direct model provider routes across OpenAI, Claude, Gemini, Grok, OSS, DeepSeek, Qwen, and Kimi.',
      'Reduced XSS and SSRF risk by sanitizing generated HTML surfaces and validating public remote media URLs before server-side fetches.',
      'Reworked protected media delivery to use Bearer-authenticated blob loading instead of access tokens in query strings.',
      'Built a centralized model registry with provider defaults, env overrides, fallback lists, readiness checks, pricing manifests, and Cloud Run/local promotion scripts.',
      'Added structured-output parsing and rendering infrastructure spanning backend metadata, persistence, and React presentation.',
    ],
  },
  {
    title: 'Also In v3.2.5 - Research Surface',
    meta: 'April 2026 work, shipped June 24, 2026 in v3.2.5.',
    intro: 'This release added a dedicated research UX and normalized source-rich model output across chat and agent flows.',
    bullets: [
      'Built React research components for live progress, source cards, source rails, source drawers, and reusable research messages.',
      'Normalized research metadata across streaming responses, final responses, conversation reloads, and Agent Mode messages.',
      'Extended backend web-search utilities and provider routes to support consistent citation/source payloads.',
      'Refactored chat rendering around explicit output surfaces: research, creative, diff, structured, and markdown fallback.',
      'Improved UX state handling for search-in-progress, answer-streaming, and completed sourced-answer states.',
    ],
  },
  {
    title: 'v3.2.4 - Claude Agent Reliability',
    meta: 'Released March 20, 2026 for Mac and Windows.',
    bullets: [
      'Improved Claude Agent Mode reliability across streaming, tool execution, context gathering, and frontend event delivery.',
      'Strengthened agent runtime modules for communication, context, script editing, LLM connectors, and writing tools.',
      'Expanded the Electron renderer bridge so agent activity could flow cleanly across chat, outline, and screenplay windows.',
    ],
  },
  {
    title: 'v3.2.3 - Build And Release Maintenance',
    meta: 'Released March 20, 2026 for Mac and Windows.',
    bullets: [
      'Maintained parallel Mac and Windows build workflows.',
      'Kept release automation, package metadata, and desktop target configuration synchronized.',
    ],
  },
  {
    title: 'v3.2.2 - Claude Runtime Fixes',
    meta: 'Released March 19, 2026 for Mac and Windows.',
    bullets: [
      'Patched Claude runtime behavior after major agent changes.',
      'Updated Python dependencies, runtime patches, database handling, and screenplay change-set logic.',
    ],
  },
  {
    title: 'v3.2.1 - Storyboard, Video, And Backend Patch',
    meta: 'Released March 5, 2026 for Mac and Windows.',
    bullets: [
      'Improved storyboard and video route behavior across backend and frontend layers.',
      'Added dev packaging configuration and release-validation artifacts.',
      'Updated server dependencies and packaged-app behavior.',
    ],
  },
  {
    title: 'v3.2.0 - Retrieval, Style Memory, And Media Capability System',
    meta: 'Released March 3, 2026 for Mac and Windows.',
    intro: 'This release introduced core context infrastructure that makes Virgil feel less like a stateless chatbot and more like a project-aware writing system.',
    bullets: [
      'Built retrieval modules for conversation context, document context, embeddings, script outlines, background jobs, metrics, and shared contracts.',
      'Added a style subsystem with ingest routes, context building, and a React style-library panel.',
      'Added video capability metadata and expanded provider route architecture, including xAI/Grok video support.',
      'Improved storyboard/media APIs and video provider handling.',
      'Added web-compatible renderer shims and development modes.',
      'Updated desktop assets, updater UX, release packaging, and build configuration.',
    ],
  },
  {
    title: 'v3.1.8 - v3.1.12 - Backend Release Train',
    meta: 'Released March 3, 2026 for Mac and Windows.',
    bullets: [
      'Shipped rapid backend and desktop patch releases while stabilizing the path to v3.2.0.',
      'Managed release metadata, backend versioning, package updates, and provider route cleanup.',
    ],
  },
  {
    title: 'v3.1.7 - Release Polish',
    meta: 'Released February 27, 2026 for Mac and Windows.',
    bullets: [
      'Continued release-hardening work across desktop packaging and metadata.',
      'Validated Mac and Windows release parity.',
    ],
  },
  {
    title: 'v3.1.6 - Branding And Updater Fixes',
    meta: 'Released February 27, 2026 for Mac and Windows.',
    bullets: [
      'Updated production app branding assets.',
      'Fixed updater behavior in packaged desktop builds.',
    ],
  },
  {
    title: 'v3.1.5 - Agent Hotfixes',
    meta: 'Released February 21, 2026 for Mac and Windows.',
    bullets: [
      'Fixed urgent Agent Mode scoping regressions.',
      'Corrected Claude environment configuration.',
      'Stabilized runtime behavior after tool execution and cancellation changes.',
    ],
  },
  {
    title: 'v3.1.4 - Desktop Updater Reliability',
    meta: 'Released February 21, 2026 for Mac and Windows.',
    bullets: [
      'Improved Electron auto-updater reliability across Mac and Windows.',
      'Tightened release packaging behavior and updater configuration.',
    ],
  },
  {
    title: 'v3.1.2 - v3.1.3 - Release Maintenance',
    meta: 'Released February 21, 2026 for Mac and Windows.',
    bullets: [
      'Shipped metadata, package, and release automation updates.',
      'Kept patch delivery moving while validating hotfixes.',
    ],
  },
  {
    title: 'v3.1.1 - Web Foundation, Cloud Persistence, UI Overhaul, And Tool Cancellation',
    meta: 'Released February 21, 2026 for Mac and Windows.',
    intro: 'This release expanded the product from local-first desktop app toward web-capable, cloud-backed creative software.',
    bullets: [
      'Added web runtime support and frontend shims for browser-compatible execution.',
      'Added cloud-save-oriented persistence across projects, conversations, and public-launch paths.',
      'Reworked large parts of the React UI across chat, project management, settings, screenplay, video generation, and app shell styling.',
      'Improved provider-specific video generation and capability handling.',
      'Added early style-library infrastructure.',
      'Improved cross-provider tool execution and cancellation.',
      'Fixed latency, vector infrastructure, AWS/media issues, outline UI regressions, and release blockers.',
    ],
  },
  {
    title: 'v3.1.0 - Multi-Provider Agent Platform',
    meta: 'Released December 19, 2025 for Mac and Windows. Also tagged as v3.1 before the semver correction.',
    intro: 'This release transformed Virgil from a provider-specific assistant into a multi-provider AI agent platform for screenwriting workflows.',
    bullets: [
      'Integrated additional model providers across Claude, OpenAI, Gemini, Grok, DeepSeek, Qwen, Kimi, and OSS Fireworks routes.',
      'Built/expanded agent tools for writing, editing, communication, outline analysis, web search, and provider connectors.',
      'Added realtime Socket.IO status plumbing for agent progress, reasoning, tool calls, and detached-window synchronization.',
      'Improved model selection UX with provider icons and custom model controls.',
      'Refactored chat and agent code paths to support multiple providers while maintaining a coherent user experience.',
    ],
  },
  {
    title: 'v3.0.1 - Windows Release Stabilization',
    meta: 'Released November 26, 2025 for Windows.',
    bullets: [
      'Fixed Windows packaging and startup issues after the initial v3.0 release.',
      'Updated model pricing and billing metadata.',
      'Improved screenplay multi-edit controls and storyboard styling.',
    ],
  },
  {
    title: 'v3.0 - Multi-Provider Video Generation',
    meta: 'Released November 20, 2025 for Mac and Windows.',
    intro: 'This release added a full media-generation vertical to an existing writing application.',
    bullets: [
      'Designed and shipped a React video generation window with dedicated styling and workflow state.',
      'Built Flask backend video infrastructure for media handling, pricing, shared routes, provider abstractions, and provider-specific adapters.',
      'Integrated support paths for OpenAI, Google, Runway, Luma, Kling, Hailuo, and related video providers.',
      'Connected generated video/media outputs into storyboard storage and retrieval flows.',
      'Extended billing/cost tracking to account for new video and model usage.',
      'Consolidated legacy editor/timeline styling while modernizing the app layout.',
    ],
  },
];

const views = {
  product: {
    label: 'Product Changelog',
    eyebrow: 'For writers and customers',
    heading: 'Product Changelog',
    description: 'For writers, creators, filmmakers, and early customers.',
    summary: PRODUCT_SUMMARY,
    entries: productEntries,
  },
  engineering: {
    label: 'Engineering Changelog',
    eyebrow: 'For employers and collaborators',
    heading: 'Engineering Changelog',
    description: 'For prospective employers, portfolio reviewers, and technical collaborators.',
    summary: ENGINEERING_SUMMARY,
    entries: engineeringEntries,
  },
};
// Visible by default; set REACT_APP_SHOW_ENGINEERING_CHANGELOG=false to hide.
const showEngineeringChangelog =
  process.env.REACT_APP_SHOW_ENGINEERING_CHANGELOG !== 'false';

function ChangelogEntry({ entry }) {
  return (
    <article className="changelog-entry">
      <h3>{entry.title}</h3>
      <p className="changelog-meta">{entry.meta}</p>
      {entry.intro && <p className="changelog-entry-intro">{entry.intro}</p>}
      <ul>
        {entry.bullets.map((bullet) => (
          <li key={bullet}>{bullet}</li>
        ))}
      </ul>
    </article>
  );
}

function ChangelogPage() {
  const [activeView, setActiveView] = useState('product');

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (
      hash === 'product' ||
      (showEngineeringChangelog && hash === 'engineering')
    ) {
      setActiveView(hash);
    }
  }, []);

  const handleViewChange = (view) => {
    setActiveView(view);
    window.history.replaceState(null, '', `/changelog#${view}`);
  };

  const view = views[activeView];
  const visibleViews = showEngineeringChangelog
    ? views
    : { product: views.product };

  return (
    <div className="page-container">
      <SEO
        title="Virgil Changelog - Product Updates and Engineering Milestones"
        description="See how Virgil evolved from a desktop screenwriting assistant into a multi-provider AI creative workspace for writers, filmmakers, and technical collaborators."
        keywords="Virgil changelog, AI writing product updates, AI creative workspace, screenwriting AI release notes, AI engineering portfolio"
      />
      <section className="content-section static-page-section changelog-page-section">
        <p className="section-eyebrow">Virgil Changelog</p>
        <h1 className="page-title page-title--wide">From desktop assistant to AI creative workspace</h1>
        <p className="page-lead page-lead--compact">
          Public-beta product updates and release notes.
        </p>

        {showEngineeringChangelog && (
        <div className="changelog-switcher" role="tablist" aria-label="Changelog audience">
          {Object.entries(visibleViews).map(([key, item]) => (
            <button
              key={key}
              type="button"
              className={`changelog-switcher-button ${activeView === key ? 'active' : ''}`}
              onClick={() => handleViewChange(key)}
              role="tab"
              aria-selected={activeView === key}
              aria-controls={`changelog-${key}`}
            >
              <span>{item.label}</span>
              <small>{item.eyebrow}</small>
            </button>
          ))}
        </div>
        )}

        <div id={`changelog-${activeView}`} className="page-content changelog-content" role="tabpanel">
          <div className="changelog-summary-card">
            <h2>{view.heading}</h2>
            <p className="changelog-view-description">{view.description}</p>
            {view.summary.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>

          <div className="changelog-list">
            {view.entries.map((entry) => (
              <ChangelogEntry key={entry.title} entry={entry} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default ChangelogPage;

